/**
 * Shared Annotate Dropdown Component
 *
 * Provides consistent "Annotate" dropdown across sections with:
 * - Add Comment/Note
 * - Add Tag
 * - Remove Tag
 *
 * Sections control business logic through handler props.
 * Component handles UI presentation and consistency.
 *
 * @example
 * ```tsx
 * // Basic usage
 * <AnnotateDropdown
 *   onCommentClick={modalHandlers.openCommentModal}
 *   onTagClick={modalHandlers.openTagModal}
 *   onRemoveTagClick={modalHandlers.openRemoveTagModal}
 * />
 *
 * // With custom labels (Assets section uses "Note")
 * <AnnotateDropdown
 *   labels={{ comment: 'Add Note' }}
 *   onCommentClick={...}
 *   onTagClick={...}
 *   onRemoveTagClick={...}
 * />
 *
 * // Hide specific actions
 * <AnnotateDropdown
 *   hideActions={{ removeTag: true }}
 *   onCommentClick={...}
 *   onTagClick={...}
 *   onRemoveTagClick={() => {}} // Still required but won't render
 * />
 * ```
 *
 * @module components/actions
 */

import { EllipsisHorizontalCircleIcon } from '@heroicons/react/24/solid';
import { useMemo } from 'react';

import { ACTION_ICONS } from '@/components/actions/constants/actionIcons';
import type { AnnotateDropdownProps } from '@/components/actions/types';
import { Dropdown } from '@/components/Dropdown';
import type { DropdownOption } from '@/types/dropdown.types';
import { cn } from '@/utils/classname';

/**
 * Default labels for annotate actions
 */
const DEFAULT_LABELS = {
  comment: 'Add Comment',
  tag: 'Add Tag',
  removeTag: 'Remove Tag',
} as const;

/**
 * Annotate Dropdown Component
 *
 * Renders a dropdown with Comment, Tag, and Remove Tag actions.
 * Handlers must be provided by parent component (section-level).
 *
 * @param props - Component props
 * @returns Dropdown component
 */
export function AnnotateDropdown({
  onCommentClick,
  onTagClick,
  onRemoveTagClick,
  labels,
  hideActions,
  disabled = false,
  className,
  'data-testid': testId = 'annotate-dropdown',
}: AnnotateDropdownProps) {
  /**
   * Build dropdown options based on configuration
   *
   * Memoized to prevent unnecessary re-renders when handlers change.
   * Only rebuilds when labels, shortcuts, or hideActions change.
   */
  const options = useMemo<DropdownOption[]>(() => {
    const items: DropdownOption[] = [];

    // Comment action
    if (!hideActions?.comment) {
      items.push({
        label: labels?.comment ?? DEFAULT_LABELS.comment,
        icon: <ACTION_ICONS.COMMENT className="size-4" />,
        onClick: onCommentClick,
        disabled: disabled,
      });
    }

    // Tag action
    if (!hideActions?.tag) {
      items.push({
        label: labels?.tag ?? DEFAULT_LABELS.tag,
        icon: <ACTION_ICONS.ADD_TAG className="size-4" />,
        onClick: onTagClick,
        disabled: disabled,
      });
    }

    // Remove Tag action
    if (!hideActions?.removeTag) {
      items.push({
        label: labels?.removeTag ?? DEFAULT_LABELS.removeTag,
        icon: <ACTION_ICONS.REMOVE_TAG className="size-4" />,
        onClick: onRemoveTagClick,
        disabled: disabled,
      });
    }

    return items;
  }, [
    labels,
    hideActions,
    disabled,
    onCommentClick,
    onTagClick,
    onRemoveTagClick,
  ]);

  return (
    <div data-testid={testId}>
      <Dropdown
        title="Annotate Options"
        size="sm"
        options={options}
        placeholder="Annotate"
        styleType="rounded"
        border={false}
        disabled={disabled}
        className={cn('[&_span:nth-child(3)]:hidden', className)}
        listClassName="!z-[150]"
        icon={<EllipsisHorizontalCircleIcon className="size-4" />}
      />
    </div>
  );
}
